/* eslint-disable react/no-array-index-key */
import React, { useEffect } from 'react';
import { Loader } from '@saviynt/design-system';

import { RequestStatus, type Response, ResponseFailure } from '../../../models';
import { isEnumMember, useInvertedLocalize } from '../../../utilities';
import { P } from '../../utilities';
import { type FactorType } from '../models';
import { type RootState, useDispatch, useSelector } from '../store';
import { thunks } from '../store';

import FactorCard from './FactorCard';
import { invertedMessages } from './messages';
import MfaError from './MfaError';

import './Menu.css';

interface MenuProps {
  onSelect: (factor: FactorType) => void;
}

interface SelectedState {
  availableFactors: Response<FactorType[]>;
  availableFactorsStatus: RequestStatus;
}

/** Retrieves and displays the available factors for the user to select */
export default function Menu({ onSelect: handleSelect }: MenuProps) {
  const { availableFactors, availableFactorsStatus } = useSelector(selectState);
  const dispatch = useDispatch();
  const l = useInvertedLocalize(invertedMessages);
  const isError = isEnumMember(availableFactors, ResponseFailure);
  const automaticSelection =
    !isError && availableFactors?.length === 1 ? availableFactors[0] : null;

  useEnsureDataLoadedEffect(availableFactors, availableFactorsStatus);
  useEffect(() => {
    if (automaticSelection) {
      handleSelect(automaticSelection);
    }
  }, [automaticSelection, handleSelect]);

  if (
    availableFactors === undefined ||
    availableFactorsStatus === RequestStatus.Pending
  ) {
    return (
      <div className='Nav_Menu Nav_Menu--isLoading'>
        <Loader kind='loop' format='main' color='brand' />
        <P>{l('Loading your authentication methods...')}</P>
      </div>
    );
  }

  if (isError) {
    const handleRetry = () => dispatch(thunks.requestAvailableFactors());

    return <MfaError onRetry={handleRetry} />;
  }

  if (automaticSelection) {
    return null;
  }

  return (
    <div className='Nav_Menu Nav_Menu--isOptions'>
      {availableFactors.map((factor) => (
        <FactorCard key={factor} item={factor} onSelect={handleSelect} />
      ))}
    </div>
  );
}

/**
 * Dispatches the available factor retrieval thunk if the data is not already
 * loaded
 */
function useEnsureDataLoadedEffect(
  availableFactors: Response<FactorType[]>,
  availableFactorsStatus: RequestStatus
) {
  const dispatch = useDispatch();

  useEffect(() => {
    if (availableFactors !== undefined) {
      return;
    }

    dispatch(thunks.requestAvailableFactors());
  }, [availableFactorsStatus, availableFactors, dispatch]);
}

function selectState({
  mfaPrompt: { availableFactors, availableFactorsStatus },
}: RootState): SelectedState {
  return {
    availableFactors,
    availableFactorsStatus,
  };
}
